import {
    IOfferingUser, IUser, IOffering, INotification, ITopic, IPeerConnection, ISearch,
    IUserSummary, IEngagementType, IConsentForm, IConsentUpload, IOfferingSummary,
    ITopicRating, IEngagementRating, IHistoricalData, IQuestion
} from "../interfaces/models";
import TopicRepository from "./TopicRepository";
import { setToken, apiFetch, apiPost } from "./APIRepository";

import { localTZToUTC } from "../util";

let IDCounter = 0;
const types = ["Provide Mentorship", "Seek Mentorship", "Find Study Partner"];

const _topics = ["Arrays", "Loops", "Recursion", "Algorithms", "Data Structures", "Variables"];

const _n = (i: number) => Math.floor(Math.random() * i);

export const makeUser = () => {
    const getType = (i: number) => {
        return types[i] as "Provide Mentorship" | "Seek Mentorship" | "Find Study Partner";
    };

    const connections = new Array(2 + _n(8)).fill(0).map(_ => {
        const connection: IPeerConnection = {
            edgeStart: 0,
            edgeEnd: 0,
            type: getType(_n(2)),
            topic: _topics[_n(6)],
            weight: _n(10),
            date: new Date(),
            availableTime: new Date()
        };
        return connection;
    });
    const proficienciesLength = 1 + _n(3);
    const proficiencies = Array.from({ length: proficienciesLength },
        _ => _topics[_n(_topics.length)]) as string[];

    const user: IUser = {
        userID: (IDCounter++).toString(),
        name: "_",
        bio: "_",
        image: "//loremflickr.com/320/240/person",

        availableTime: new Date(),
        proficiencies: proficiencies,
        connections: connections
    };
    return user;
};

const _defaultSearch = () => ({
    sortField: "recommended",
    sortDesc: true,
    filterField: "unanswered",
    query: "",
    page: 0,
    pageSize: 25,
    filterTopics: []
});

const _searchCaches: Map<string, ISearch> = new Map<string, ISearch>();
let _currentCourse = "";
const _userConsentMap: Map<string, boolean | undefined> = new Map<string, boolean | undefined>();
export default class UserRepository {
    static userHasConsentedForCourse(): Promise<boolean> {
        if (_userConsentMap.has(_currentCourse)) {
            return Promise.resolve(_userConsentMap.get(_currentCourse) !== undefined);
        } else {
            return apiFetch<boolean | undefined>(`/users/consent/`)
                .then(x => {
                    if (x !== undefined && typeof x !== "boolean") {
                        x = undefined;
                    }

                    _userConsentMap.set(_currentCourse, x);

                    return x !== undefined;
                });
        }
    }

    static getUserStats(startDate?: Date | undefined, endDate?: Date): Promise<IUserSummary[]> {
        return apiFetch<IUserSummary[]>(`/users/stats/all/s${startDate == undefined? "": localTZToUTC(startDate)}/`
            + `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getConsentedUserStats(startDate?: Date, endDate?: Date): Promise<IUserSummary[]> {
        return apiFetch<IUserSummary[]>(`/users/stats/consented/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getUserTopicReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/user_topic/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getUserTopicHistoryReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/user_topic_history/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getQuestionReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/question/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getAnswerReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/answer/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getCommentReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/comment/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getConsentReport(startDate?: Date, endDate?: Date): Promise<string> {
        return apiFetch<string>(`/users/reports/consent/` +
            `s${startDate == undefined? "": localTZToUTC(startDate)}/` +
            `e${endDate == undefined? "": localTZToUTC(endDate)}`);
    }

    static getOfferingStats(): Promise<IOfferingSummary> {
        return apiFetch<IOfferingSummary>(`/users/stats/offering/`);
    }

    static getAuthoredQuestions(userID: number, pageSize: number, pageIndex: number):
        Promise<{ questions: IQuestion[], totalItems: number }> {
        return apiFetch<{ questions: IQuestion[], totalItems: number }>(
            `/users/authored/${userID}/${pageSize}/${pageIndex}/`);
    }

    static getLoggedInUser(): Promise<IOfferingUser> {
        return apiFetch<IOfferingUser>(`/users/me/`);
    }

    static getConsentForm(): Promise<IConsentForm> {
        return apiFetch<IConsentForm>(`/users/consent_form/`);
    }

    static getUserConsentFormResponse(): Promise<boolean | undefined> {
        const cachedUserConsent = _userConsentMap.get(_currentCourse);
        if (cachedUserConsent !== undefined) {
            return Promise.resolve(cachedUserConsent);
        }

        return apiFetch<boolean | undefined>(`/users/consent/`)
            .then(x => {
                if (x === undefined || typeof x == "boolean") {
                    return x;
                }
                return undefined;
            });
    }

    static getUserOfferings(): Promise<IOffering[]> {
        return apiFetch<IOffering[]>(`/users/offerings/`);
    }

    static getUserConnections(count: number): Promise<IUser[]> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(Array.from({ length: count }, makeUser));
            }, Math.random() * 1000);
        });
    }

    static getUserNotifications(): Promise<INotification[]> {
        return apiFetch<INotification[]>(`/users/notifications/all/`);
    }

    static markNotificationAsRead(id: number): Promise<void> {
        return apiFetch<void>(`/users/notifications/read/` + id);
    }

    static getAllAvailableCategories(): Promise<string[]> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(types.slice());
            }, Math.random() * 1000);
        });
    }

    static getAllAvailableEngagementTypes(): Promise<IEngagementType[]> {
        return apiFetch<IEngagementType[]>(`/users/engagement/`)
            .then(topics => topics.map(x => TopicRepository.engagementPointer(x)));
    }

    static getUserLeaderboard(sortField: string, sortOrder: "DESC" | "ASC",
            leaderboardStartDate?: Date, leaderboardEndDate?: Date): Promise<IUserSummary[]> {
        let sentStartDate:string = "";
        let sentEndDate:string = "";
        if (leaderboardStartDate) {
            sentStartDate = "" + localTZToUTC(leaderboardStartDate);
        }
        if (leaderboardEndDate) {
            let endDate = new Date(leaderboardEndDate);
            //End date should be inclusive - add one to the day
            endDate = new Date(endDate.setDate(endDate.getDate()+1));
            sentEndDate = "" + localTZToUTC(endDate);
        }
        return apiFetch<IUserSummary[]>(
            `/questions/leaderboard/?sort_field=${sortField}&sort_order=${sortOrder}` +
            `&start_date=${sentStartDate}&end_date=${sentEndDate}`);
    }

    static getCompareAgainst(): Promise<ITopicRating[]> {
        return apiFetch<ITopicRating[]>(`/questions/competencies/aggregate/`);
    }

    static getUserCompetencies(userID: number): Promise<ITopicRating[]> {
        if (userID === undefined) {
            userID = -1;
        }
        return apiFetch<ITopicRating[]>(`/questions/competencies/all/${userID}`);
    }

    static getHistoricalCompetencies(userID: number): Promise<IHistoricalData> {
        if (userID === undefined) {
            userID = -1;
        }
        return apiFetch<IHistoricalData>(`/questions/historical/${userID}`);
    }

    static getEngagementAgainst(compareTo: string): Promise<IEngagementRating[]> {
        return apiFetch<IEngagementRating[]>(`/users/engagement/aggregate/${compareTo}/`);
    }

    //Using undefined paramter because of the old engagement summary module
    static getUserEngagement(userID: number | undefined): Promise<IEngagementRating[]> {
        if (userID === undefined) {
            userID = -1;
        }
        return apiFetch<IEngagementRating[]>(`/users/engagement/all/${userID}`);
    }

    static getMeetingHistory(): Promise<{name: string, id: number }[]> {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(["Toowong", "UQ", "University Of Queensland",
                    "Kenmore", "Indro"].map((x, i) => ({ name: x, id: i })));
            }, Math.random() * 1000);
        });
    }

    static setCurrentToken(token: string) {
        setToken(token);
    }

    static authenticate(courseCode?: string): Promise<boolean> {
        return apiFetch<{token: string, courseID: string}>(`/users/login/${courseCode || " "}`)
            .then(x => {
                setToken(x.token);
                _currentCourse = x.courseID;
                return this.userHasConsentedForCourse().then(
                    function(x: boolean) {
                        if (x) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                );
            });
    }

    static getSearchCacheForCourse() {
        const cache = _searchCaches.get(_currentCourse);
        if (!cache) {
            _searchCaches.set(_currentCourse, _defaultSearch());
        }
        return Promise.resolve(_searchCaches.get(_currentCourse)!);
    }

    static setSearchCacheForCourse(search: ISearch) {
        const arrCopy = search.filterTopics.slice();
        _searchCaches.set(_currentCourse, Object.assign({}, search, { filterTopics: arrCopy }));
    }

    static updateOffering(offering: IOffering, topics: ITopic[]): Promise<IOfferingUser> {
        return apiPost<IOfferingUser>(`/users/offerings/update/`, { offering, topics });
    }

    static updateUserImage(newImage: string): Promise<IUser> {
        return apiPost<IUser>(`/users/me/image/`, {
            image: newImage
        });
    }

    static uploadConsentForm(consentForm: IConsentUpload): Promise<string> {
        return apiFetch<string>(`/users/submit_consent_form/`, {
            method: "POST",
            headers: new Headers({
                "Accept": "application/json",
                "Content-Type": "application/json"
            }),
            body: JSON.stringify(consentForm)
        });
    }

    static sendConsent(response: boolean): Promise<boolean> {
        _userConsentMap.set(_currentCourse, response);
        return apiFetch<{response: boolean}>(`/users/consent/`, {
            method: "POST",
            headers: new Headers({
                "Accept": "application/json",
                "Content-Type": "application/json"
            }),
            body: JSON.stringify({ "response": response })
        })
            .then(function(x) {
                return x.response;
            });
    }

    static getUniversities(): Promise<string[]> {
        return apiFetch<string[]>(`/users/universities/`);
    }

    static getAllOfferingUsers(offeringID: string): Promise<IOfferingUser[]> {
        return apiFetch<IOfferingUser[]>(`/users/get_all_offering_users/${offeringID}`);
    }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader!./src/repositories/UserRepository.ts